import getTokenIDsFromString from '../get-token-id-from-string';
import isEmpty from '../is-empty';

/**
 * Extracts and returns the token parts from a tokenID string, while ignoring ':' inside nested tokens (i.e., within double curly brackets {{...}}).
 * It correctly handles nested curly braces and ensures that colons within them are not treated as delimiters.
 *
 * @param {String} tokenID - The token string to parse.
 * @return {String[]} - An array of the token parts.
 */
const getTokenParts = tokenID => {
	// If the tokenID is empty, return an empty array
	if ( isEmpty( tokenID ) ) {
		return [];
	}

	// Step 1: Extract the top-level tokens (i.e., the nested token structures)
	const tokens = getTokenIDsFromString( tokenID );

	// Step 2: Replace nested tokens in the original string with placeholders
	let placeholderID = 0; // Unique ID to create placeholders
	const placeholderMap = new Map(); // Map to store the placeholders and their corresponding tokens
	let cleanedTokenID = tokenID; // Copy of the original tokenID to manipulate

	// Use regular expressions to replace the nested tokens with placeholders only when surrounded by colons
	tokens.forEach( token => {
		// Wrap the extracted token back with double curly brackets to match its full form.
		token = `{{${ token }}}`;

		// Create a unique placeholder for this token (e.g., {{__TOKEN_0__}}).
		const placeholder = `__TOKEN_${ placeholderID++ }__`;

		// Replace each occurrence of the token with its unique placeholder.
		// This ensures we can safely split the string without affecting the tokens inside curly braces.
		cleanedTokenID = cleanedTokenID.replace( token, placeholder );

		// Store the placeholder-token pair in the Map for later restoration.
		placeholderMap.set( placeholder, token );
	} );

	// Step 3: Split the string by colons outside curly braces using the cleaned version
	const rawParts = cleanedTokenID.split( /:(?![^{}]*\}\})/ );

	// Step 4: Replace placeholders back with the original nested tokens
	const finalParts = rawParts.map( part => {
		// Replace any placeholder in the part with the corresponding original token
		let restoredPart = part;
		placeholderMap.forEach( ( original, placeholder ) => {
			restoredPart = restoredPart.replace( placeholder, original );
		} );

		return restoredPart;
	} );

	return finalParts; // Return the final array of token parts
};

export default getTokenParts;
